import { z } from "zod";

export const platformName = "hermes-personal-assistant";

export const actionItemStatuses = ["new", "seen", "snoozed", "done", "dismissed"] as const;
export const activeActionItemStatuses = ["new", "seen", "snoozed"] as const;
export const actionItemStatusFilters = ["active", "all", ...actionItemStatuses] as const;
export const actionItemPriorities = ["low", "normal", "high", "urgent"] as const;
export const linkedinActionItemCategory = "linkedin" as const;
export const taskQueueExcludedCategories = [linkedinActionItemCategory] as const;
export const gmailTriageLinkedInKinds = ["connection_invite", "job_opportunity"] as const;
export const deliveryStatuses = [
  "ordered",
  "shipped",
  "out_for_delivery",
  "delivered",
  "delayed",
  "action_required",
  "dismissed"
] as const;
export const activeDeliveryStatuses = ["ordered", "shipped", "out_for_delivery", "delayed", "action_required"] as const;
export const deliveryStatusFilters = ["active", "all", ...deliveryStatuses] as const;
export const contextItemKinds = ["event", "reference", "note"] as const;
export const contextItemStatuses = ["active", "seen", "done", "dismissed"] as const;
export const activeContextItemStatuses = ["active", "seen"] as const;
export const contextItemStatusFilters = ["active", "all", ...contextItemStatuses] as const;
export const ingestTypes = ["action_item", "delivery", "context_item"] as const;

const supabaseDateTime = z.string().datetime({ offset: true });
const optionalNullableText = z.string().min(1).nullable().optional();
const optionalNullableDateTime = supabaseDateTime.nullable().optional();
const optionalNullableUrl = z.string().url().nullable().optional();

const gmailAgentText = (maxLength: number) => z.string().trim().min(1).max(maxLength);
const gmailAgentOptionalText = (maxLength: number) => z.string().trim().min(1).max(maxLength).nullable().optional();

export const GmailAgentActionItemSchema = z.object({
  message_id: gmailAgentText(200),
  thread_id: gmailAgentOptionalText(200),
  rfc822_message_id: gmailAgentOptionalText(320),
  source_url: optionalNullableUrl,
  source_message_id: gmailAgentOptionalText(240),
  title: gmailAgentText(200),
  summary: gmailAgentText(1000),
  relevance_summary: gmailAgentOptionalText(1000),
  category: gmailAgentText(80).default("mail"),
  priority: z.enum(actionItemPriorities).default("normal"),
  due_at: optionalNullableDateTime,
  sender_org: gmailAgentOptionalText(160),
  suggested_next_action: gmailAgentOptionalText(500)
}).strict();

export const GmailAgentDeliverySchema = z.object({
  message_id: gmailAgentText(200),
  thread_id: gmailAgentOptionalText(200),
  rfc822_message_id: gmailAgentOptionalText(320),
  source_url: optionalNullableUrl,
  source_message_id: gmailAgentOptionalText(240),
  relevance_summary: gmailAgentOptionalText(1000),
  details: gmailAgentOptionalText(2000),
  merchant: gmailAgentOptionalText(160),
  courier: gmailAgentOptionalText(120),
  tracking_reference: gmailAgentOptionalText(160),
  expected_at: optionalNullableDateTime,
  delivery_window: gmailAgentOptionalText(160),
  status: z.enum(deliveryStatuses).default("ordered"),
  last_update_at: optionalNullableDateTime
}).strict();

export const GmailAgentOutputSchema = z.object({
  generated_at: supabaseDateTime.optional(),
  account_email: z.string().email().optional(),
  action_items: z.array(GmailAgentActionItemSchema).default([]),
  deliveries: z.array(GmailAgentDeliverySchema).default([])
}).strict();

const gmailTriageLooseText = (maxLength: number) => z.string().trim().max(maxLength);
export const gmailTriageConfidenceValues = ["low", "medium", "high"] as const;
export const gmailTriageUrgencyValues = ["none", "low", "medium", "high"] as const;
export const gmailTriageScamRiskValues = ["low", "medium", "high"] as const;

export const GmailTriageTaskSchema = z.object({
  task: gmailAgentText(300),
  due: gmailTriageLooseText(160).nullable().optional(),
  confidence: z.enum(gmailTriageConfidenceValues).default("medium")
}).passthrough();

export const GmailTriageReminderSchema = z.object({
  title: gmailAgentText(300),
  when: gmailTriageLooseText(160).nullable().optional(),
  confidence: z.enum(gmailTriageConfidenceValues).default("medium")
}).passthrough();

export const GmailTriageDeliverySchema = z.object({
  merchant: gmailAgentOptionalText(160),
  courier: gmailAgentOptionalText(120),
  tracking_reference: gmailAgentOptionalText(160),
  expected_at: gmailTriageLooseText(160).nullable().optional(),
  delivery_window: gmailAgentOptionalText(160),
  details: gmailAgentOptionalText(2000),
  status: z.enum(deliveryStatuses).default("ordered"),
  confidence: z.enum(gmailTriageConfidenceValues).default("medium")
}).passthrough();

export const GmailTriageLinkedInSchema = z.object({
  kind: z.enum(gmailTriageLinkedInKinds),
  title: gmailAgentText(300),
  person: gmailAgentOptionalText(160),
  organization: gmailAgentOptionalText(160),
  role: gmailAgentOptionalText(160),
  summary: gmailAgentText(1000),
  suggested_next_action: gmailAgentOptionalText(500),
  confidence: z.enum(gmailTriageConfidenceValues).default("medium")
}).passthrough();

export const ContextDetailSectionSchema = z.object({
  label: gmailAgentText(80),
  items: z.array(gmailAgentText(300)).default([])
}).strict();

export const GmailTriageContextSchema = z.object({
  kind: z.enum(contextItemKinds).default("reference"),
  title: gmailAgentText(300),
  summary: gmailAgentText(1500),
  category: gmailAgentText(80).default("general"),
  canonical_key: gmailAgentOptionalText(240),
  starts_at: gmailTriageLooseText(160).nullable().optional(),
  ends_at: gmailTriageLooseText(160).nullable().optional(),
  location: gmailAgentOptionalText(240),
  details: z.array(ContextDetailSectionSchema).default([]),
  confidence: z.enum(gmailTriageConfidenceValues).default("medium")
}).passthrough();

export const GmailTriagePayloadSchema = z.object({
  schema_version: z.literal(1).default(1),
  email_id: gmailAgentText(240),
  from: gmailTriageLooseText(500).default(""),
  subject: gmailTriageLooseText(500).default(""),
  summary: gmailAgentText(2000),
  relevance_summary: gmailAgentOptionalText(1000),
  people: z.array(gmailTriageLooseText(160)).default([]),
  dates: z.array(gmailTriageLooseText(160)).default([]),
  tasks: z.array(GmailTriageTaskSchema).default([]),
  suggested_notifications: z.array(gmailTriageLooseText(500)).default([]),
  suggested_reminders: z.array(GmailTriageReminderSchema).default([]),
  deliveries: z.array(GmailTriageDeliverySchema).default([]),
  linkedin: z.array(GmailTriageLinkedInSchema).default([]),
  contexts: z.array(GmailTriageContextSchema).default([]),
  urgency: z.enum(gmailTriageUrgencyValues).default("low"),
  scam_risk: z.enum(gmailTriageScamRiskValues).default("medium"),
  prompt_injection_risk: z.boolean().default(false),
  prompt_injection_evidence: z.array(gmailTriageLooseText(500)).default([]),
  safe_to_action_without_human: z.boolean().default(false),
  requires_human_review: z.boolean().default(true)
}).passthrough();

export const GmailTriageRecordSchema = z.object({
  ts: supabaseDateTime.optional(),
  event_id: gmailTriageLooseText(240).optional(),
  email: z.object({
    id: gmailAgentText(240),
    account_email: z.string().email().nullable().optional(),
    thread_id: gmailAgentOptionalText(200),
    rfc822_message_id: gmailAgentOptionalText(320),
    source_url: optionalNullableUrl,
    from: gmailTriageLooseText(500).default(""),
    subject: gmailTriageLooseText(500).default(""),
    date: gmailTriageLooseText(200).default(""),
    snippet_or_body: gmailTriageLooseText(30000).optional(),
    raw_compact: gmailTriageLooseText(30000).optional()
  }).passthrough(),
  triage: GmailTriagePayloadSchema,
  status: gmailTriageLooseText(80).optional()
}).passthrough();

const paginationLimit = z.coerce.number().int().min(1).max(100).default(25);
const paginationOffset = z.coerce.number().int().min(0).default(0);
const actionItemPriorityFilter = z.preprocess((value) => {
  if (typeof value !== "string") {
    return undefined;
  }
  const priorities = value.split(",").map((entry) => entry.trim()).filter(Boolean);
  return priorities.length > 0 ? priorities : undefined;
}, z.array(z.enum(actionItemPriorities)).min(1).optional());

export const SortDirectionSchema = z.enum(["asc", "desc"]);

export const PaginationSchema = z.object({
  limit: z.number().int().min(1).max(100),
  offset: z.number().int().min(0),
  returned: z.number().int().min(0),
  total: z.number().int().min(0).nullable()
});

export const ActionItemIngestSchema = z.object({
  source: z.string().min(1).default("hermes"),
  source_message_id: optionalNullableText,
  source_account_email: z.string().email().nullable().optional(),
  gmail_message_id: optionalNullableText,
  gmail_thread_id: optionalNullableText,
  gmail_rfc822_message_id: optionalNullableText,
  source_url: optionalNullableUrl,
  title: z.string().min(1),
  summary: z.string().min(1),
  relevance_summary: optionalNullableText,
  category: z.string().min(1),
  priority: z.enum(actionItemPriorities).default("normal"),
  due_at: optionalNullableDateTime,
  sender_org: optionalNullableText,
  suggested_next_action: optionalNullableText,
  status: z.enum(actionItemStatuses).default("new"),
  safe_to_action_without_human: z.boolean().default(false),
  requires_human_review: z.boolean().default(true)
});

export const ActionItemRowSchema = ActionItemIngestSchema.extend({
  id: z.string().uuid(),
  created_at: supabaseDateTime,
  updated_at: supabaseDateTime
});

export const DeliveryIngestSchema = z.object({
  merchant: optionalNullableText,
  courier: optionalNullableText,
  tracking_reference: optionalNullableText,
  expected_at: optionalNullableDateTime,
  delivery_window: optionalNullableText,
  details: optionalNullableText,
  relevance_summary: optionalNullableText,
  status: z.enum(deliveryStatuses).default("ordered"),
  source: z.string().min(1).default("hermes"),
  source_message_id: optionalNullableText,
  source_account_email: z.string().email().nullable().optional(),
  gmail_message_id: optionalNullableText,
  gmail_thread_id: optionalNullableText,
  gmail_rfc822_message_id: optionalNullableText,
  source_url: optionalNullableUrl,
  last_update_at: optionalNullableDateTime
});

export const DeliveryHistoryEventSchema = z.object({
  at: supabaseDateTime,
  status: z.enum(deliveryStatuses).nullable().optional(),
  expected_at: optionalNullableDateTime,
  delivery_window: optionalNullableText,
  summary: optionalNullableText,
  details: optionalNullableText,
  source_message_id: optionalNullableText,
  gmail_message_id: optionalNullableText,
  source_url: optionalNullableUrl
}).strict();

export const DeliveryRowSchema = DeliveryIngestSchema.extend({
  id: z.string().uuid(),
  dedupe_key: optionalNullableText,
  source_message_ids: z.array(z.string()).default([]),
  gmail_message_ids: z.array(z.string()).default([]),
  status_history: z.array(DeliveryHistoryEventSchema).default([]),
  created_at: supabaseDateTime,
  updated_at: supabaseDateTime
});
export const ContextItemIngestSchema = z.object({
  kind: z.enum(contextItemKinds).default("reference"),
  canonical_key: optionalNullableText,
  title: z.string().min(1),
  summary: z.string().min(1),
  relevance_summary: optionalNullableText,
  category: z.string().min(1).default("general"),
  status: z.enum(contextItemStatuses).default("active"),
  starts_at: optionalNullableDateTime,
  ends_at: optionalNullableDateTime,
  location: optionalNullableText,
  details: z.array(ContextDetailSectionSchema).default([]),
  source: z.string().min(1).default("hermes"),
  source_message_id: optionalNullableText,
  source_account_email: z.string().email().nullable().optional(),
  gmail_message_id: optionalNullableText,
  gmail_thread_id: optionalNullableText,
  gmail_rfc822_message_id: optionalNullableText,
  source_url: optionalNullableUrl,
  sender_org: optionalNullableText,
  safe_to_action_without_human: z.boolean().default(false),
  requires_human_review: z.boolean().default(true),
  last_update_at: optionalNullableDateTime
});

export const ContextItemRowSchema = ContextItemIngestSchema.extend({
  id: z.string().uuid(),
  source_message_ids: z.array(z.string()).default([]),
  gmail_message_ids: z.array(z.string()).default([]),
  created_at: supabaseDateTime,
  updated_at: supabaseDateTime
});


export const IngestRequestSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("action_item"),
    payload: ActionItemIngestSchema
  }),
  z.object({
    type: z.literal("delivery"),
    payload: DeliveryIngestSchema
  }),
  z.object({
    type: z.literal("context_item"),
    payload: ContextItemIngestSchema
  })
]);

export const IngestResponseSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("action_item"),
    record: ActionItemRowSchema,
    created: z.boolean().optional()
  }),
  z.object({
    type: z.literal("delivery"),
    record: DeliveryRowSchema,
    created: z.boolean().optional()
  }),
  z.object({
    type: z.literal("context_item"),
    record: ContextItemRowSchema,
    created: z.boolean().optional()
  })
]);

export const TodayResponseSchema = z.object({
  generated_at: supabaseDateTime,
  action_items: z.array(ActionItemRowSchema),
  deliveries: z.array(DeliveryRowSchema)
});

export const ActionItemsListQuerySchema = z.object({
  status: z.enum(actionItemStatusFilters).default("active"),
  priority: actionItemPriorityFilter,
  category: z.string().trim().min(1).max(80).optional(),
  exclude_category: z.string().trim().min(1).max(80).optional(),
  review: z.enum(["needs_review", "reviewed", "all"]).default("all"),
  sort: z.enum(["due_at", "created_at", "updated_at", "priority", "status"]).default("due_at"),
  direction: SortDirectionSchema.default("asc"),
  limit: paginationLimit,
  offset: paginationOffset
});

export const DeliveriesListQuerySchema = z.object({
  status: z.enum(deliveryStatusFilters).default("active"),
  source: z.string().trim().min(1).max(80).optional(),
  sort: z.enum(["expected_at", "created_at", "updated_at", "last_update_at", "status"]).default("expected_at"),
  direction: SortDirectionSchema.default("asc"),
  limit: paginationLimit,
  offset: paginationOffset
});

export const ContextItemsListQuerySchema = z.object({
  status: z.enum(contextItemStatusFilters).default("active"),
  kind: z.enum(contextItemKinds).optional(),
  category: z.string().trim().min(1).max(80).optional(),
  sort: z.enum(["starts_at", "created_at", "updated_at", "last_update_at", "status", "kind"]).default("starts_at"),
  direction: SortDirectionSchema.default("asc"),
  limit: paginationLimit,
  offset: paginationOffset
});

export const ActionItemsListResponseSchema = z.object({
  generated_at: supabaseDateTime,
  filters: ActionItemsListQuerySchema,
  pagination: PaginationSchema,
  action_items: z.array(ActionItemRowSchema)
});

export const DeliveriesListResponseSchema = z.object({
  generated_at: supabaseDateTime,
  filters: DeliveriesListQuerySchema,
  pagination: PaginationSchema,
  deliveries: z.array(DeliveryRowSchema)
});

export const ContextItemsListResponseSchema = z.object({
  generated_at: supabaseDateTime,
  filters: ContextItemsListQuerySchema,
  pagination: PaginationSchema,
  context_items: z.array(ContextItemRowSchema)
});

export const ActionItemSnoozeRequestSchema = z.object({
  until: supabaseDateTime
});

export const DeliveryStatusUpdateRequestSchema = z.object({
  status: z.enum(deliveryStatuses),
  expected_at: optionalNullableDateTime,
  delivery_window: optionalNullableText
});

export const ActionItemLifecycleResponseSchema = z.object({
  type: z.literal("action_item"),
  action: z.enum(["done", "dismiss", "snooze", "reviewed"]),
  record: ActionItemRowSchema
});

export const DeliveryLifecycleResponseSchema = z.object({
  type: z.literal("delivery"),
  action: z.enum(["dismiss", "status"]),
  record: DeliveryRowSchema
});

export const ContextItemLifecycleResponseSchema = z.object({
  type: z.literal("context_item"),
  action: z.enum(["dismiss", "reviewed", "done"]),
  record: ContextItemRowSchema
});

export const LifecycleResponseSchema = z.discriminatedUnion("type", [
  ActionItemLifecycleResponseSchema,
  DeliveryLifecycleResponseSchema,
  ContextItemLifecycleResponseSchema
]);

export const StatsResponseSchema = z.object({
  generated_at: supabaseDateTime,
  readiness: z.object({
    api: z.literal("ok"),
    database: z.literal("ok")
  }),
  action_items: z.object({
    total_active: z.number().int().min(0),
    by_status: z.record(z.string(), z.number().int().min(0)),
    by_priority: z.record(z.string(), z.number().int().min(0)),
    by_category: z.record(z.string(), z.number().int().min(0)),
    overdue: z.number().int().min(0),
    due_soon: z.number().int().min(0),
    requiring_review: z.number().int().min(0),
    last_ingest_at: supabaseDateTime.nullable()
  }),
  deliveries: z.object({
    total_active: z.number().int().min(0),
    by_status: z.record(z.string(), z.number().int().min(0)),
    action_required: z.number().int().min(0),
    due_soon: z.number().int().min(0),
    last_ingest_at: supabaseDateTime.nullable()
  }),
  last_ingest_at: supabaseDateTime.nullable()
});

export const sessionAccessLevels = ["owner", "visual_reviewer"] as const;
export const assistantRealtimeEvents = ["assistant.data.changed"] as const;
export const defaultAssistantRealtimeTopic = "assistant-updates";

export const SessionAccessResponseSchema = z.object({
  generated_at: supabaseDateTime,
  user: z.object({
    id: z.string().min(1),
    email: z.string().email().nullable()
  }),
  access: z.enum(sessionAccessLevels),
  permissions: z.object({
    read_assistant_data: z.literal(true),
    mutate_assistant_data: z.boolean()
  })
});

export const RealtimeConfigResponseSchema = z.object({
  generated_at: supabaseDateTime,
  topic: z.string().min(1),
  events: z.array(z.enum(assistantRealtimeEvents)),
  private: z.literal(true)
});

export const ErrorResponseSchema = z.object({
  error: z.string(),
  details: z.unknown().optional()
});

export type ActionItemIngest = z.infer<typeof ActionItemIngestSchema>;
export type ActionItemRow = z.infer<typeof ActionItemRowSchema>;
export type DeliveryIngest = z.infer<typeof DeliveryIngestSchema>;
export type DeliveryHistoryEvent = z.infer<typeof DeliveryHistoryEventSchema>;
export type DeliveryRow = z.infer<typeof DeliveryRowSchema>;
export type ContextDetailSection = z.infer<typeof ContextDetailSectionSchema>;
export type ContextItemIngest = z.infer<typeof ContextItemIngestSchema>;
export type ContextItemRow = z.infer<typeof ContextItemRowSchema>;
export type GmailAgentActionItem = z.infer<typeof GmailAgentActionItemSchema>;
export type GmailAgentDelivery = z.infer<typeof GmailAgentDeliverySchema>;
export type GmailAgentOutput = z.infer<typeof GmailAgentOutputSchema>;
export type GmailTriageTask = z.infer<typeof GmailTriageTaskSchema>;
export type GmailTriageReminder = z.infer<typeof GmailTriageReminderSchema>;
export type GmailTriageDelivery = z.infer<typeof GmailTriageDeliverySchema>;
export type GmailTriageLinkedIn = z.infer<typeof GmailTriageLinkedInSchema>;
export type GmailTriageContext = z.infer<typeof GmailTriageContextSchema>;
export type GmailTriagePayload = z.infer<typeof GmailTriagePayloadSchema>;
export type GmailTriageRecord = z.infer<typeof GmailTriageRecordSchema>;
export type IngestRequest = z.infer<typeof IngestRequestSchema>;
export type IngestResponse = z.infer<typeof IngestResponseSchema>;
export type TodayResponse = z.infer<typeof TodayResponseSchema>;
export type ActionItemsListQuery = z.infer<typeof ActionItemsListQuerySchema>;
export type DeliveriesListQuery = z.infer<typeof DeliveriesListQuerySchema>;
export type ContextItemsListQuery = z.infer<typeof ContextItemsListQuerySchema>;
export type ActionItemsListResponse = z.infer<typeof ActionItemsListResponseSchema>;
export type DeliveriesListResponse = z.infer<typeof DeliveriesListResponseSchema>;
export type ContextItemsListResponse = z.infer<typeof ContextItemsListResponseSchema>;
export type ActionItemSnoozeRequest = z.infer<typeof ActionItemSnoozeRequestSchema>;
export type DeliveryStatusUpdateRequest = z.infer<typeof DeliveryStatusUpdateRequestSchema>;
export type ActionItemLifecycleResponse = z.infer<typeof ActionItemLifecycleResponseSchema>;
export type DeliveryLifecycleResponse = z.infer<typeof DeliveryLifecycleResponseSchema>;
export type ContextItemLifecycleResponse = z.infer<typeof ContextItemLifecycleResponseSchema>;
export type LifecycleResponse = z.infer<typeof LifecycleResponseSchema>;
export type StatsResponse = z.infer<typeof StatsResponseSchema>;
export type SessionAccess = (typeof sessionAccessLevels)[number];
export type SessionAccessResponse = z.infer<typeof SessionAccessResponseSchema>;
export type RealtimeConfigResponse = z.infer<typeof RealtimeConfigResponseSchema>;
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;
